import { cx } from '@linaria/core';
import { useAuthState } from 'modules/Auth/AuthContext';
import { useFeaturedFlags } from 'modules/FeaturedFlags/useFeaturedFlags';
import { useOpenCreateOrgModal } from 'modules/Organizations/hooks';
import { TextTooltip } from 'components/Tooltip';
import { IconPlus } from 'components/Icons';
import { getMaxOrganizations } from 'modules/Organizations/constants';
import { tooltipCls } from 'modules/Models/components/ModelViewer/styles';
import { Button } from 'components/ui_library/Button';
import { noop } from 'utils/fp';
import { useIsInternalUser } from 'modules/Auth/hooks';
import { createOrgButton } from './styles';

type CreateNewOrgButtonProps = {
  customButton?: React.FC<React.HTMLAttributes<HTMLButtonElement>>;
  /**
   * If its used inside a drawer or modal, this function will be called to close it after the button is clicked.
   */
  onClose?: () => void;
  className?: string;
};

export function CreateNewOrgButton({ onClose, customButton, className }: CreateNewOrgButtonProps) {
  const { authData } = useAuthState();
  const internalUser = useIsInternalUser();
  const [hasOrgAccess, canCreateMultipleOrgs] = useFeaturedFlags(['OrgCreateOrganization', 'MultiOrganization']);
  const openCreateOrgModal = useOpenCreateOrgModal();
  const RenderButton = customButton || Button;

  const maximumOrganizations = getMaxOrganizations(internalUser);
  const orgsList = authData?.organizations?.map(({ organization }) => organization) || [];
  const isMaxOrgsReached = orgsList?.length >= maximumOrganizations;
  const allowedToCreateMultiOrgs = canCreateMultipleOrgs && orgsList?.length < maximumOrganizations;
  const isDisabled = isMaxOrgsReached || (orgsList.length === 1 && !allowedToCreateMultiOrgs);

  const showTooltipText =
    orgsList.length === 1 && !allowedToCreateMultiOrgs
      ? 'Multiple Organizations feature is available on the Enterprise plan'
      : isMaxOrgsReached
      ? 'You reached the maximum number of Organizations'
      : null;

  const openModalAndCloseDrawer = () => {
    openCreateOrgModal();
    onClose?.();
  };

  if (!hasOrgAccess) {
    return null;
  }

  return (
    <TextTooltip hidden={!showTooltipText} showArrow text={showTooltipText} className={tooltipCls}>
      <RenderButton
        data-testid="navbar-create-org-btn"
        variant="unstyled"
        onClick={isDisabled ? noop : openModalAndCloseDrawer}
        disabled={isDisabled}
        className={cx(createOrgButton, 'fullWidth', className)}
      >
        <IconPlus size={20} />
        Create new Organization
      </RenderButton>
    </TextTooltip>
  );
}
